import { type Module } from "vuex";
import type { AxiosRequestConfig } from "axios";
import type { DefaultApi, ResumeListResponse, ResumeRequest } from "@/api";
import { Resume } from "@/models/resume";
import { ResumeSyncStatus } from "@/models/resume_sync_status";
import { Resumes } from "@/models/resumes";
import type { State as RootState } from "@/stores";
export interface State {
  resumes?: Resumes;
  listLoadingByPosition: Record<number, boolean>;
  syncStatusesByPosition: Record<number, ResumeSyncStatus>;
}
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    resumes: undefined,
    listLoadingByPosition: {},
    syncStatusesByPosition: {},
  },
  actions: {
    async postResumes(
      {},
      {
        positionId,
        file,
        onUploadProgress,
        signal,
      }: {
        positionId: number;
        file: File;
        onUploadProgress?: AxiosRequestConfig["onUploadProgress"];
        signal?: AbortSignal;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postResumes(positionId, file, {
        onUploadProgress,
        signal,
      });
      return response.data;
    },
    async getResumes({ commit }, positionId: number) {
      commit("setListLoading", { positionId, loading: true });
      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getResumes(positionId);
        const resumes = new Resumes(
          response.data.map(
            (resume: Partial<ResumeListResponse>) => new Resume(resume),
          ),
        );
        commit("setResumes", resumes);
        return resumes;
      } catch (error) {
        // Do not retain similarity flags when the current user can no longer
        // confirm which resumes are visible.
        commit("clearSimilarityFlags");
        throw error;
      } finally {
        commit("setListLoading", { positionId, loading: false });
      }
    },
    async getResume(
      { commit },
      {
        positionId,
        resumeId,
      }: {
        positionId: number;
        resumeId: number;
      },
    ) {
      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getResume(positionId, resumeId);
        const resume = new Resume(response.data);
        commit("setResume", resume);
        return resume;
      } catch (error) {
        // Do not retain similarity evidence fetched under permissions that may
        // no longer hold.
        commit("clearResumeSimilarityMatches", resumeId);
        throw error;
      }
    },
    async patchResume(
      { commit },
      {
        resumeId,
        positionId,
        resumeRequest,
        cancelQueuedInvite,
      }: {
        positionId: number;
        resumeId: number;
        resumeRequest: ResumeRequest;
        cancelQueuedInvite?: boolean;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchResume(
        positionId,
        resumeId,
        resumeRequest,
        cancelQueuedInvite,
      );
      const resume = new Resume(response.data);
      commit("setResume", resume);
      if ("archived" in resumeRequest)
        commit("position/invalidatePositions", null, { root: true });
      return resume;
    },
    async getSyncResumeStatus({ commit }, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getSyncResumeStatus(positionId);
      const syncStatus = new ResumeSyncStatus(response.data);
      commit("setSyncStatus", {
        positionId,
        syncStatus,
      });
      return syncStatus;
    },
    async postResumeSync({}, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      await api.postResumeSync(positionId);
      return;
    },
  },
  mutations: {
    setListLoading(
      state,
      { positionId, loading }: { positionId: number; loading: boolean },
    ) {
      state.listLoadingByPosition = {
        ...state.listLoadingByPosition,
        [positionId]: loading,
      };
    },
    setResume(state, resume: Resume) {
      if (state.resumes === undefined) state.resumes = new Resumes([resume]);
      else state.resumes = state.resumes.update(resume);
    },
    setResumes(state, resumes: Resumes) {
      state.resumes = resumes;
    },
    setSyncStatus(
      state,
      {
        positionId,
        syncStatus,
      }: {
        positionId: number;
        syncStatus: ResumeSyncStatus;
      },
    ) {
      state.syncStatusesByPosition = {
        ...state.syncStatusesByPosition,
        [positionId]: syncStatus,
      };
    },
    // Fail-closed for the individual resume's similarity evidence.
    clearResumeSimilarityMatches(state, resumeId: number) {
      if (state.resumes === undefined) return;
      const existing = state.resumes.get(resumeId);
      if (existing === undefined) return;
      state.resumes = state.resumes.update(
        new Resume({
          ...existing,
          similarity_matches: [],
        }),
      );
    },
    // Fail-closed for the list's similarity flag on every retained (possibly stale) row; other fields are left as-is.
    clearSimilarityFlags(state) {
      if (state.resumes === undefined) return;
      state.resumes = new Resumes(
        state.resumes.map(
          (resume) =>
            new Resume({
              ...resume,
              has_similarity_signal: false,
            }),
        ),
      );
    },
  },
};
